import { FC, useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import { Box, Button, CircularProgress, Container, Typography, Drawer, Skeleton, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Stack, Paper, Divider } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import jwt_decode from 'jwt-decode';
import { fetchInvoiceById, fetchInvoicePaymentReceiptPreview, fetchInvoices, deletePaymentReceipt } from '../../store/invoiceStore/invoiceThunk';
import { deleteVendorPayment } from '../../store/CashOutflowStore/cashOutflowThunk';
import { InvoicePreviewPayload } from "../../utils/invoice.types";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { Utils, VocuherCategory } from "../../utils/common-utils";
// import HomeHeader from "../SignIn/HomeHeader"; // logo hidden on the shared receipt view
import DialogShareInvoiceReciept from '../../components/DialogShareInvoiceReciept/DialogShareInvoiceReciept';
import ShareIcon from '@mui/icons-material/Share';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InvoiceNavigationBar from '../../components/Invoice/InvoiceNavigationBar';
import { PermissionIconButton } from '../../components/Permission';

const actionBtnStyle = { float: 'left', color: '#545454', backgroundColor: '#f5f5f5', border: 'none', height: 35, width: 35, marginTop: 0, marginRight: 20, marginLeft: -5 } as const;

// URL carries type=AR|AP (receipt links) or sales|purchase (invoice links) — normalise to VocuherCategory
const toVoucherCategory = (type: string | null): string => {
  if (type === 'AP' || type === VocuherCategory.PURCHASE || type === 'purchases') return VocuherCategory.PURCHASE;
  return VocuherCategory.SALE;
};
const toReceiptTypeParam = (vCategory: string): string => (vCategory === VocuherCategory.PURCHASE ? 'AP' : 'AR');

// Only invoices that have at least one payment can have a receipt — prev/next & search stay within these
const RECEIPT_STATUS_FILTER = '&status=PAID,PARTIALLY_PAID,OVER_PAID';
const RECEIPT_STATUS_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'PAID', label: 'Paid' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
  { value: 'OVER_PAID', label: 'Over Paid' },
];

const formatDate = (d: any): string => (d ? new Date(d).toLocaleDateString('en-IN') : 'N/A');
const statusColor = (status?: string): string => {
  if (status === 'PAID') return 'success.main';
  if (status === 'OVER_PAID') return 'info.main';
  return 'warning.main';
};
const statusLabel = (status?: string): string => (status || 'PENDING').replace(/_/g, ' ');
const toNumber = (v: any): number => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const invoiceTotal = (inv: any): number =>
  inv?.finalAmount != null ? toNumber(inv.finalAmount) : toNumber(inv?.amount) + toNumber(Utils.additionalChargesSum(inv));

// Payment Receipt preview page — same navigation toolbar as the invoice preview page.
const InvoicePaymentReceiptPreview: FC = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("templateId");
  const voucherType = toVoucherCategory(searchParams.get("type"));

  const { isLoggedIn, selectedAccount } = useAppSelector((state) => state.auth);
  const invoiceDetails = useAppSelector(state => state.invoices.invoiceDetails);
  const { invoices } = useAppSelector(state => state.invoices);

  const [pdfLocation, setPdfLocation] = useState<string>("");
  const [invoicePreview, setInvoicePreviewResponse] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);
  const [isRefersh, setIsRefersh] = useState("");
  const [currentInvoiceId, setCurrentInvoiceId] = useState(invoiceId);
  // Latest invoice id for async callbacks (list fetch resolves after the id may have changed)
  const currentInvoiceIdRef = useRef(currentInvoiceId);
  currentInvoiceIdRef.current = currentInvoiceId;

  // Pagination states (for prev/next navigation)
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageSize = 20;

  const [openShareInvoiceRecieptDrawer, setOpenShareInvoiceRecieptDrawer] = useState<boolean>(false);
  const [openDeleteReceiptsDialog, setOpenDeleteReceiptsDialog] = useState<boolean>(false);
  const [isDeletingReceipts, setIsDeletingReceipts] = useState<boolean>(false);

  const openShareInvoiceDrawer = (event: any) => {
    setOpenShareInvoiceRecieptDrawer(true);
    event.stopPropagation();
  };

  const handleCloseDrawer = () => {
    setOpenShareInvoiceRecieptDrawer(false);
  };

  // Back to the invoice preview of the current invoice
  const handleViewInvoice = () => {
    if (!currentInvoiceId) return;
    const invTemplate = invoiceDetails?.pdfTemplate || '';
    navigate(`/invoice/${currentInvoiceId}/preview?templateId=${invTemplate}&type=${voucherType}`);
  };

  // Delete all receipts applied to the current invoice (same thunks as the action menu)
  const handleConfirmDeleteReceipts = async () => {
    if (!currentInvoiceId) return;
    setIsDeletingReceipts(true);
    try {
      const vCategory = invoiceDetails?.voucherCategory || voucherType;
      if (vCategory === VocuherCategory.SALE) {
        await dispatch(deletePaymentReceipt(currentInvoiceId));
      } else {
        await dispatch(deleteVendorPayment(currentInvoiceId));
      }
      dispatch(fetchInvoiceById(currentInvoiceId));
      setIsRefersh(Math.random().toString());
    } catch (error) {
      console.error('Error deleting receipts:', error);
    } finally {
      setIsDeletingReceipts(false);
      setOpenDeleteReceiptsDialog(false);
    }
  };

  // Receipt-only toolbar actions
  const receiptActions = (
    <>
      <IconButton onClick={handleViewInvoice} style={actionBtnStyle} size='small'>
        <Tooltip arrow title={`View ${invoiceDetails?.voucherType || 'Invoice'}`}><DescriptionIcon color="info" style={{ fontSize: 20 }} /></Tooltip>
      </IconButton>
      <IconButton onClick={(e) => openShareInvoiceDrawer(e)} style={actionBtnStyle} size='small'>
        <Tooltip arrow title="Share Receipt"><ShareIcon color="success" style={{ fontSize: 20 }} /></Tooltip>
      </IconButton>
      <PermissionIconButton module="payments" action="delete"
        title="Delete All Receipts"
        deniedMessage="You do not have permission to delete payment receipts"
        onClick={() => setOpenDeleteReceiptsDialog(true)}
        style={actionBtnStyle}
        size='small'>
        <ReceiptLongIcon color="error" style={{ fontSize: 20 }} />
      </PermissionIconButton>
    </>
  );

  // Update currentInvoiceId when URL params change
  useEffect(() => {
    if (invoiceId && invoiceId !== currentInvoiceId) {
      setCurrentInvoiceId(invoiceId);
      setIsLoading(true);
    }
  }, [invoiceId]);

  // Fetch invoices in batches for navigation
  const fetchInvoiceBatch = async (page: number = 1, append: boolean = false) => {
    const { _id: accountId } = selectedAccount;
    if (accountId && isLoggedIn) {
      const skip = (page - 1) * pageSize;
      if (append) setLoadingMore(true);

      try {
        const result: any = await dispatch(fetchInvoices({
          accountId,
          type: voucherType,
          params: `&limit=${pageSize}&skip=${skip}${RECEIPT_STATUS_FILTER}`
        }));
        const newInvoices = result.payload?.invoices || [];
        setHasMoreData(newInvoices.length === pageSize);
        // fetchInvoices.fulfilled resets invoiceDetails in the slice — reload the current
        // invoice's details so the sidebar breakdown isn't wiped by the list response.
        if (currentInvoiceIdRef.current) {
          dispatch(fetchInvoiceById(currentInvoiceIdRef.current));
        }
      } catch (error) {
        console.error('Error fetching invoices:', error);
        setHasMoreData(false);
      } finally {
        if (append) setLoadingMore(false);
      }
    }
  };

  // Initial load
  useEffect(() => {
    setCurrentPage(1);
    fetchInvoiceBatch(1, false);
  }, [selectedAccount._id, isLoggedIn, voucherType]);

  // Load current invoice details (toolbar actions + sidebar breakdown) when ID changes
  useEffect(() => {
    if (currentInvoiceId && isLoggedIn) {
      dispatch(fetchInvoiceById(currentInvoiceId));
    }
  }, [currentInvoiceId, dispatch, isLoggedIn]);

  // Load the receipt PDF
  useEffect(() => {
    const getPreview = async () => {
      let reqObj: InvoicePreviewPayload = { invoiceId: currentInvoiceId, templateId: templateId };
      let invoicePreviewResponse: any = await dispatch(fetchInvoicePaymentReceiptPreview(reqObj));

      let location = null;
      if (invoicePreviewResponse?.payload && !invoicePreviewResponse?.error) {
        if (invoicePreviewResponse.payload.length > 0) {
          location = invoicePreviewResponse.payload[0].Location;
        } else {
          location = invoicePreviewResponse.payload.Location;
        }
        setInvoicePreviewResponse(invoicePreviewResponse.payload);
      } else {
        setInvoicePreviewResponse(undefined);
      }
      setPdfLocation(location || '');
      setIsLoading(false);
    };

    if (templateId && currentInvoiceId) {
      getPreview();
    } else if (currentInvoiceId) {
      try {
        let token: any = jwt_decode(currentInvoiceId);
        setPdfLocation(token.pdfLcation);
        setIsLoading(false);
      } catch (error) {
        console.log("Failed! Invalid Token", error);
        setIsLoading(false);
      }
    }
  }, [currentInvoiceId, isRefersh, templateId, dispatch]);

  const actionChangeHandler = (data: any) => {
    setIsRefersh(data.isRefersh);
  };

  // Navigate to another invoice's receipt (prev / next / search)
  const handleInvoiceChange = (newInvoiceId: string, _newTemplateId: string, vCategory: string) => {
    if (newInvoiceId !== currentInvoiceId) {
      setCurrentInvoiceId(newInvoiceId);
      setIsLoading(true);
      setPdfLocation('');

      if (isLoggedIn) {
        dispatch(fetchInvoiceById(newInvoiceId));
      }

      // Receipts use the invoice's receipt template, not the invoice template
      const target: any = invoices?.find((inv: any) => inv._id === newInvoiceId);
      const receiptTemplateId = target?.receiptPdfTemplate || templateId || '';
      const newCategory = vCategory === VocuherCategory.PURCHASE ? VocuherCategory.PURCHASE : VocuherCategory.SALE;

      navigate(`/invoice-payment-receipt/${newInvoiceId}/view?templateId=${receiptTemplateId}&type=${toReceiptTypeParam(newCategory)}`, {
        replace: true
      });
    }
  };

  const handleLoadMore = () => {
    if (hasMoreData && !loadingMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchInvoiceBatch(nextPage, true);
    }
  };

  // Direct invoice-number search (the "#" button)
  const handleDirectInvoiceSearch = async (invoiceNumber: string): Promise<boolean> => {
    if (!invoiceNumber.trim()) return false;

    const { _id: accountId } = selectedAccount;
    if (accountId && isLoggedIn) {
      try {
        const result: any = await dispatch(fetchInvoices({
          accountId,
          type: voucherType,
          params: `&invoiceNumber=${encodeURIComponent(invoiceNumber)}${RECEIPT_STATUS_FILTER}`
        }));
        const foundInvoices = result.payload?.invoices || [];

        if (foundInvoices.length > 0) {
          const firstInvoice = foundInvoices[0];
          await dispatch(fetchInvoiceById(firstInvoice._id));
          handleInvoiceChange(
            firstInvoice._id,
            firstInvoice.receiptPdfTemplate || templateId || '',
            firstInvoice.voucherCategory || voucherType
          );
          return true;
        } else {
          alert(`No paid invoices found matching "${invoiceNumber}". Please try a different search.`);
          return false;
        }
      } catch (error) {
        console.error('Error searching invoice:', error);
        alert('Error searching for invoice. Please try again.');
        return false;
      }
    }
    return false;
  };

  // Logged-out (shared link) view — unchanged
  if (!isLoggedIn) {
    return (
      isLoading ?
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
        :
        <Grid container rowSpacing="24" columnSpacing="5">
          <Container component="main" >
            <div style={{ "width": "70vw" }}>
              {/* Logo + Login/Signup hidden on the shared (email / WhatsApp) receipt link view
              <div className="preview">
                <HomeHeader />
              </div>
              <Box onClick={() => { navigate("/") }}
                style={{ "float": "right", "marginTop": "-42" }}
                sx={{ cursor: "pointer" }}>
                <Button disableElevation sx={{ mt: "20px" }} size="large" type="submit" variant="contained" color="primary">
                  Login/Signup
                </Button>
              </Box>
              */}
              <p style={{ "clear": "both" }} />
            </div>
            <p style={{ "clear": "both" }}></p>
            <Box>
              <object style={{ height: "80vh", width: "70vw" }} data={pdfLocation} type="application/pdf" />
            </Box>
          </Container>
        </Grid>
    );
  }

  return (
    <>
      <Box sx={{ backgroundColor: '#fafafb', minHeight: '100vh', pt: 0, pb: 2 }}>
        {/* Navigation Bar and Actions — same toolbar as invoice preview */}
        <Box sx={{ p: { xs: 1, sm: 2 }, pb: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              {(invoices && invoices.length > 0 && currentInvoiceId) && (
                <InvoiceNavigationBar
                  currentInvoiceId={currentInvoiceId || ''}
                  voucherCategory={invoiceDetails?.voucherCategory || ''}
                  voucherType={voucherType}
                  onInvoiceChange={handleInvoiceChange}
                  invoices={invoices}
                  currentInvoiceDetails={invoiceDetails}
                  onActionChange={actionChangeHandler}
                  onLoadMore={handleLoadMore}
                  hasMoreData={hasMoreData}
                  loadingMore={loadingMore}
                  onDirectSearch={handleDirectInvoiceSearch}
                  actions={receiptActions}
                  entityLabel="Receipt"
                  statusOptions={RECEIPT_STATUS_OPTIONS}
                />
              )}
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2} sx={{ p: { xs: 1, sm: 2 } }}>
          <Grid item xs={12} md={8} sx={{ order: { xs: 2, md: 1 }, mt: { xs: 2, md: 0 } }}>
            {(isLoading ?
              <Box sx={{ backgroundColor: '#fff', borderRadius: '12px', p: 3, height: { xs: '60vh', md: '85vh' } }}>
                <Grid container>
                  <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <CircularProgress color="info" />
                    <Typography sx={{ mt: 2 }}>Loading Preview ...</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton width="100%" height={300} />
                  </Grid>
                </Grid>
              </Box>
              :
              <Box sx={{ backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden', height: { xs: '60vh', md: '85vh' } }}>
                {pdfLocation ? (
                  <object style={{ height: "100%", width: "100%" }} data={pdfLocation} type="application/pdf" />
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                    <ReceiptLongIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 1 }} />
                    <Typography color="text.secondary">No payment receipt available for this invoice</Typography>
                    <Typography variant="caption" color="text.secondary">Record a payment first to generate a receipt</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Grid>

          {/* Right side - Quick Stats and Receipt Details (same breakdown as invoice preview) */}
          <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>

            {/* Quick Stats */}
            <Grid item xs={12} md={12} sx={{ pb: 2 }}>
              {invoices && invoices.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    m: 0,
                    border: '1px solid #e9ecf1',
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                    <Box sx={{ flex: 1, textAlign: 'center', borderRight: '1px solid #e9ecf1', pr: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.25, lineHeight: 1.2 }}>
                        {invoices.length}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.75rem' }}>
                        Total
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, textAlign: 'center', borderRight: '1px solid #e9ecf1', px: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', mb: 0.25, lineHeight: 1.2 }}>
                        {invoices.filter((inv: any) => inv.status === 'PAID').length}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.75rem' }}>
                        Paid
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, textAlign: 'center', borderRight: '1px solid #e9ecf1', px: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main', mb: 0.25, lineHeight: 1.2 }}>
                        {invoices.filter((inv: any) => inv.status === 'PARTIALLY_PAID').length}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.75rem' }}>
                        Partial
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, textAlign: 'center', pl: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main', mb: 0.25, lineHeight: 1.2 }}>
                        {invoices.filter((inv: any) => inv.status === 'OVER_PAID').length}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.75rem' }}>
                        Over Paid
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}
            </Grid>

            {/* Receipt / Party Details */}
            <Box sx={{
              borderRadius: '12px',
              border: '1px solid #e9ecf1',
              backgroundColor: '#fff',
              display: { xs: 'none', sm: 'block' }
            }}>
              <Box sx={{
                bgcolor: '#f5f5f5',
                p: { xs: 1.5, sm: 2 },
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                  Receipt Preview
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                  {formatDate(invoiceDetails?.date)} • #{invoiceDetails?.invoiceNumber || ''} • {statusLabel(invoiceDetails?.status)}
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack spacing={{ xs: 1.5, sm: 2 }}>

                  <Box>
                    <Typography variant="body2" color="text.secondary">Party Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {invoiceDetails?.buyer?.name || invoiceDetails?.supplier?.name || invoiceDetails?.client?.name || 'N/A'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="body2" color="text.secondary">Invoice Amount</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {Utils.formatNumber(invoiceTotal(invoiceDetails).toFixed(2))}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">Received Amount</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main' }}>
                      {Utils.formatNumber(toNumber(invoiceDetails?.receivedAmount).toFixed(2))}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">Balance Amount</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: toNumber(invoiceDetails?.balanceAmount) > 0 ? 'warning.main' : 'text.primary' }}>
                      {Utils.formatNumber(toNumber(invoiceDetails?.balanceAmount).toFixed(2))}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">Invoice Date</Typography>
                    <Typography variant="body1">{formatDate(invoiceDetails?.date)}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: statusColor(invoiceDetails?.status) }}>
                      {statusLabel(invoiceDetails?.status)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">Invoice Type</Typography>
                    <Typography variant="body1">{invoiceDetails?.voucherType || 'N/A'}</Typography>
                  </Box>

                </Stack>
              </Box>
            </Box>

          </Grid>
        </Grid>
      </Box>

      <Drawer
        disableEscapeKeyDown={false}
        anchor="right"
        onClose={handleCloseDrawer}
        open={openShareInvoiceRecieptDrawer}>
        <DialogShareInvoiceReciept
          dispatchType="Payment Receipt"
          invoiceId={currentInvoiceId || ''}
          invoicePreviewData={invoicePreview}
          selectedBuyer={invoiceDetails?.client || invoiceDetails?.buyer || invoiceDetails?.supplier || {}}
          onClose={handleCloseDrawer}
          openDrawer={openShareInvoiceRecieptDrawer}>
        </DialogShareInvoiceReciept>
      </Drawer>

      {/* Delete Receipts Confirmation */}
      <Dialog open={openDeleteReceiptsDialog} onClose={() => !isDeletingReceipts && setOpenDeleteReceiptsDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete All Receipts?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete all payment receipts applied to invoice <b>{invoiceDetails?.invoiceNumber || ''}</b>. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteReceiptsDialog(false)} disabled={isDeletingReceipts}>Cancel</Button>
          <Button onClick={handleConfirmDeleteReceipts} color="error" variant="contained" disableElevation disabled={isDeletingReceipts}>
            {isDeletingReceipts ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvoicePaymentReceiptPreview;
